import { useState, type FormEvent } from 'react';
import { Navigate, Link } from 'react-router-dom';
import {
  AFTER_AUTH_PATH,
  AUTH_BASE,
  AuthError,
  authConfigured,
  signIn,
  signUp,
} from '../api/auth';
import { useSession, writeSessionToken } from '../api/token';
import { Card } from '../ui/Card';

type Mode = 'signin' | 'signup';

const MIN_PASSWORD_LENGTH = 8;

/**
 * `/sign-in` — the first-party sign-in surface (callout-auth, email + password).
 *
 * Replaces Clerk's hosted component AND the "auth not configured" dead end it used to fall back
 * to. Both halves of the loop live here: sign-up (POST /api/auth/signup) and sign-in
 * (POST /api/auth/login). The returned bearer token goes into the EXISTING session plumbing
 * (`writeSessionToken`), so every other screen picks it up through `useTokenSource` — no second
 * token path.
 *
 * Errors are shown verbatim from the issuer's envelope: the worker already returns
 * "Email or password is incorrect" for both a wrong password and an unknown address, so the form
 * cannot be used to enumerate accounts (see workers/auth/src/index.ts).
 */
export function SignInRoute() {
  const session = useSession();
  const [mode, setMode] = useState<Mode>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Already signed in (or just signed in — the store flips during submit): go where the work is.
  if (session.status === 'signed-in') return <Navigate to={AFTER_AUTH_PATH} replace />;

  if (!authConfigured()) {
    return (
      <Card title="Sign-in unavailable">
        <p className="text-sm" data-testid="auth-unavailable">
          This build has no auth issuer configured, so it cannot issue a session. Set{' '}
          <code className="font-mono">VITE_AUTH_BASE</code> to the callout-auth origin and rebuild.
        </p>
      </Card>
    );
  }

  const expiryNotice = session.status === 'expired';

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const issued =
        mode === 'signin'
          ? await signIn({ email, password })
          : await signUp({ email, password, name });
      // Store through the shared plumbing; the redirect above fires on the re-render it causes.
      writeSessionToken(issued.token);
    } catch (caught) {
      const message =
        caught instanceof AuthError
          ? caught.message
          : 'Sign-in failed — check your connection and try again';
      setError(message);
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto flex w-full max-w-md flex-col gap-4 py-4" data-testid="signin-page">
      <header>
        <h1 className="text-xl font-semibold tracking-tight">
          {mode === 'signin' ? 'Sign in' : 'Create your account'}
        </h1>
        <p className="mt-1 text-sm text-charcoal-500">
          Email and password against our own sign-in service — no third-party login.
        </p>
      </header>

      {expiryNotice && (
        <p
          role="alert"
          data-testid="signin-expired-notice"
          className="rounded border border-copper-300 bg-copper-300/30 px-3 py-2 text-sm text-charcoal-900"
        >
          Your previous session has ended. Sign in again to keep tagging.
        </p>
      )}

      {error && (
        <p
          role="alert"
          data-testid="auth-error"
          className="rounded border border-red-300 bg-red-50 px-3 py-2 text-sm font-medium text-red-800"
        >
          {error}
        </p>
      )}

      <form
        onSubmit={onSubmit}
        data-testid={mode === 'signin' ? 'signin-form' : 'signup-form'}
        data-auth-mode={mode}
        className="space-y-3 rounded-lg border border-chalk-200 bg-white/70 p-5 shadow-sm"
      >
        {mode === 'signup' && (
          <label className="block text-sm">
            <span className="font-medium">Name (optional)</span>
            <input
              id="name"
              name="name"
              data-testid="auth-name"
              type="text"
              autoComplete="name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              className="mt-1 w-full rounded border border-charcoal-500/30 bg-white px-3 py-2 font-mono text-sm"
            />
          </label>
        )}

        <label className="block text-sm">
          <span className="font-medium">Email</span>
          <input
            id="email"
            name="email"
            data-testid="auth-email"
            type="email"
            required
            autoComplete="username"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            className="mt-1 w-full rounded border border-charcoal-500/30 bg-white px-3 py-2 font-mono text-sm"
          />
        </label>

        <label className="block text-sm">
          <span className="font-medium">Password</span>
          <input
            id="password"
            name="password"
            data-testid="auth-password"
            type="password"
            required
            minLength={MIN_PASSWORD_LENGTH}
            autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            className="mt-1 w-full rounded border border-charcoal-500/30 bg-white px-3 py-2 font-mono text-sm"
          />
          {mode === 'signup' && (
            <span className="mt-1 block text-xs text-charcoal-500">
              At least {MIN_PASSWORD_LENGTH} characters.
            </span>
          )}
        </label>

        <button
          type="submit"
          data-testid="auth-submit"
          disabled={busy}
          className="w-full rounded bg-copper px-3 py-2 font-semibold text-charcoal disabled:opacity-60"
        >
          {busy
            ? mode === 'signin'
              ? 'Signing in…'
              : 'Creating account…'
            : mode === 'signin'
              ? 'Sign in'
              : 'Create account'}
        </button>

        <p className="text-center text-xs text-charcoal-500">
          {mode === 'signin' ? 'No account yet?' : 'Already have an account?'}{' '}
          <button
            type="button"
            data-testid="auth-mode-toggle"
            onClick={() => {
              setMode(mode === 'signin' ? 'signup' : 'signin');
              setError(null);
            }}
            className="text-copper-700 underline"
          >
            {mode === 'signin' ? 'Create one' : 'Sign in instead'}
          </button>
        </p>
      </form>

      <p className="text-center text-xs text-charcoal-500" data-testid="auth-issuer">
        issuer: <code className="font-mono">{AUTH_BASE}</code>
      </p>

      <p className="text-center text-xs text-charcoal-500">
        <Link to="/privacy" className="text-copper-700 underline">
          Privacy
        </Link>
      </p>
    </div>
  );
}
